import { getApiBaseUrl } from '@config/api';
import { getWebOrigin } from '@lib/platform';

/**
 * Resolve a probe URL against the correct origin for the current runtime.
 *
 * Probe fetches use bare `fetch()` (NOT axios), so they don't inherit
 * `apiConfig.baseURL`. On native (Capacitor), bare relative URLs resolve to
 * `capacitor://localhost` (iOS) or `https://localhost` (Android), which is
 * the bundled local app, not the network. This helper picks the right base
 * for each path:
 *
 *   /favicon.ico      -> web origin (the user-facing web app domain)
 *   /api/maintenance  -> web origin (Cloudflare Pages Function lives there)
 *   /health           -> backend API origin
 *
 * On web, getWebOrigin() returns `window.location.origin` and getApiBaseUrl()
 * returns `/` (relative) for dev OR for production-without-VITE_API_BASE_URL.
 * In dev, Vite proxies `/health` to the backend (see vite.config.ts). In
 * production, Cloudflare Pages proxies `/health` via `functions/health.js` —
 * a thin pass-through (no geolocation headers, no proxy secret) because the
 * endpoint has no business logic. On native, getApiBaseUrl() returns the
 * absolute backend host and concatenation produces a direct backend URL.
 *
 * Note: probes deliberately bypass the axios `MaintenanceError` short-circuit
 * (which only covers `/api/v1/*` axios calls). `/health` is naturally outside
 * that prefix, so the probe path is doubly safe — bare fetch() AND non-v1
 * URL.
 */
export function resolveProbeUrl(path: '/favicon.ico' | '/api/maintenance' | '/health'): string {
  if (path === '/health') {
    // getApiBaseUrl() always ends with '/'. Strip leading slash from path
    // to avoid double-slash in the concatenated URL.
    return getApiBaseUrl() + path.slice(1);
  }
  // Web origin (no trailing slash) + leading-slash path = correct URL.
  return getWebOrigin() + path;
}
